// src/utils/cache/image-cache.js
// Image preloading cache - leverages browser's native HTTP cache
// Single source of truth for image preloading

import { LOGGING } from '/settings.js';

/**
 * Image Preloading Cache - Tracks which images have been preloaded
 * Uses browser's native HTTP cache (not sessionStorage)
 */
const preloadedImageCache = new Set();
// Track in-flight requests to prevent duplicate fetches for the same image
const inFlightRequests = new Set();

/**
 * Preloads images to leverage browser's native HTTP cache.
 * This prevents re-fetching when carousel/lightbox restarts.
 * Returns a Promise that resolves when all images are loaded (or failed).
 * 
 * @param {Array} images - Array of image objects with `id` property
 * @param {Function} getImagePaths - Function that takes (imageId, format) and returns {webp, jpg} paths
 * @param {string} cachePrefix - Prefix for cache keys (e.g., 'carousel', 'lightbox')
 * @param {boolean} [logPreload=false] - Whether to log preload actions (for debugging)
 * @returns {Promise<void>} - Resolves when all images are preloaded
 */
export function preloadImages(images, getImagePaths, cachePrefix = 'image', logPreload = false) {
    if (!images || !Array.isArray(images) || images.length === 0) {
        return Promise.resolve();
    }

    const preloadPromises = [];

    images.forEach(imgData => {
        const imageId = imgData.id;
        if (!imageId) return;

        // Create cache key from prefix and image ID
        const cacheKey = `${cachePrefix}_${imageId}`;

        // Skip if already preloaded or currently fetching
        if (preloadedImageCache.has(cacheKey) || inFlightRequests.has(cacheKey)) {
            if (logPreload && LOGGING.ENABLED && LOGGING.DEBUG) {
                console.log(`[Image Cache] Skipping already preloaded/in-flight: ${cacheKey}`);
            }
            return;
        }

        inFlightRequests.add(cacheKey);

        try {
            // Get image paths using the provided function
            const imagePaths = getImagePaths(imageId, 'webp');

            // Preload WebP and wait for it to actually load (so browser cache is ready)
            // Use fetch() first to verify the response is actually an image (not HTML 404 page)
            const preloadPromise = (async () => {
                try {
                    // First, check if the image exists and is actually an image (not HTML)
                    const response = await fetch(imagePaths.webp, { method: 'HEAD' });
                    const contentType = response.headers.get('content-type') || '';

                    // If server returns HTML (404 page), skip preloading to avoid network noise
                    if (!contentType.startsWith('image/')) {
                        if (logPreload && LOGGING.ENABLED && LOGGING.DEBUG) {
                            console.warn(`[Image Cache] Skipping preload for ${imageId} (${cachePrefix}) - server returned ${contentType} instead of image`);
                        }
                        // Don't mark as preloaded, so carousel can handle it gracefully
                        inFlightRequests.delete(cacheKey);
                        return;
                    }

                    // If it's an image, proceed with Image() preloading
                    return new Promise((resolve) => {
                        const preloadWebP = new Image();

                        preloadWebP.onload = () => {
                            // Verify it's actually an image by checking dimensions
                            // If width/height are 0, it might be HTML masquerading as image
                            if (preloadWebP.naturalWidth === 0 && preloadWebP.naturalHeight === 0) {
                                if (logPreload && LOGGING.ENABLED && LOGGING.DEBUG) {
                                    console.warn(`[Image Cache] Image ${imageId} (${cachePrefix}) loaded but has 0 dimensions - likely HTML`);
                                }
                                inFlightRequests.delete(cacheKey);
                                resolve(); // Don't mark as preloaded
                                return;
                            }

                            // Mark as preloaded only after successful load and verification
                            preloadedImageCache.add(cacheKey);
                            inFlightRequests.delete(cacheKey);
                            if (logPreload && LOGGING.ENABLED && LOGGING.DEBUG) {
                                console.log(`[Image Cache] Preloaded image ${imageId} (${cachePrefix}) - WebP`);
                            }
                            resolve();
                        };

                        preloadWebP.onerror = () => {
                            // Don't mark as preloaded on error - let carousel handle it
                            if (logPreload && LOGGING.ENABLED && LOGGING.DEBUG) {
                                console.warn(`[Image Cache] Failed to preload image ${imageId} (${cachePrefix})`);
                            }
                            inFlightRequests.delete(cacheKey);
                            resolve(); // Resolve anyway to not block other images
                        };

                        // Start loading
                        preloadWebP.src = imagePaths.webp;
                    });
                } catch (error) {
                    // If HEAD request fails, try direct Image() loading anyway
                    // (some servers don't support HEAD, or CORS might block it)
                    return new Promise((resolve) => {
                        const preloadWebP = new Image();

                        preloadWebP.onload = () => {
                            // Verify dimensions
                            if (preloadWebP.naturalWidth === 0 && preloadWebP.naturalHeight === 0) {
                                inFlightRequests.delete(cacheKey);
                                resolve();
                                return;
                            }

                            preloadedImageCache.add(cacheKey);
                            inFlightRequests.delete(cacheKey);
                            if (logPreload && LOGGING.ENABLED && LOGGING.DEBUG) {
                                console.log(`[Image Cache] Preloaded image ${imageId} (${cachePrefix}) - WebP`);
                            }
                            resolve();
                        };

                        preloadWebP.onerror = () => {
                            if (logPreload && LOGGING.ENABLED && LOGGING.DEBUG) {
                                console.warn(`[Image Cache] Failed to preload image ${imageId} (${cachePrefix})`);
                            }
                            inFlightRequests.delete(cacheKey);
                            resolve();
                        };

                        preloadWebP.src = imagePaths.webp;
                    });
                }
            })();

            preloadPromises.push(preloadPromise);
        } catch (error) {
            console.warn(`[Image Cache] Failed to preload image ${imageId}:`, error);
            inFlightRequests.delete(cacheKey);
        }
    });

    // Wait for all images to load (or fail)
    return Promise.all(preloadPromises).then(() => {
        if (logPreload && LOGGING.ENABLED && LOGGING.DEBUG) {
            console.log(`[Image Cache] Finished preloading ${preloadPromises.length} images (${cachePrefix})`);
        }
    });
}

/**
 * Preloads all carousel images for smooth navigation.
 * Preloads all images in the carousel when the popup opens.
 * Skips images that are already rendered in the DOM to avoid duplication.
 * 
 * @param {Array} images - Array of image objects with `id` property
 * @param {boolean} [logPreload=false] - Whether to log preload actions (for debugging)
 * @param {HTMLElement} [container=null] - Optional container to check for existing images
 * @returns {Promise<void>} - Resolves when all images are preloaded
 */
export async function preloadAllCarouselImages(images, logPreload = false, container = null) {
    if (!images || !Array.isArray(images) || images.length === 0) {
        return Promise.resolve();
    }

    try {
        // Dynamically import image utils to avoid circular dependencies
        const { getCardImagePaths } = await import('../image-utils.js');

        const preloadPromises = images.map((imageData) => {
            if (!imageData || !imageData.id) {
                return Promise.resolve();
            }

            const imageId = imageData.id;
            const cacheKey = `carousel_${imageId}`;

            // Skip if already preloaded or in-flight
            if (preloadedImageCache.has(cacheKey) || inFlightRequests.has(cacheKey)) {
                return Promise.resolve();
            }

            // Skip if already rendered in DOM (to avoid duplicate requests)
            if (container) {
                const existingImage = container.querySelector(`.carrousel-image[data-image-id="${imageId}"]`);
                if (existingImage) {
                    // Image is already in DOM, don't preload it again
                    // Mark as preloaded since it's there
                    preloadedImageCache.add(cacheKey);
                    return Promise.resolve();
                }
            }

            inFlightRequests.add(cacheKey);
            const imagePaths = getCardImagePaths(imageId, 'webp');

            // Use fetch() for preloading instead of new Image()
            // This makes the initiator show as "image-cache.js" instead of "Other"
            // The images are cached, so subsequent DOM <img> requests will be cache hits
            return fetch(imagePaths.webp, {
                method: 'GET',
                cache: 'force-cache' // Force browser to use cache
            }).then(() => {
                preloadedImageCache.add(cacheKey);
                inFlightRequests.delete(cacheKey);
                if (logPreload && LOGGING.ENABLED && LOGGING.DEBUG) {
                    console.log(`[Image Cache] Preloaded carousel image ${imageId}`);
                }
            }).catch(() => {
                // Don't mark as preloaded on error - let carousel handle it
                inFlightRequests.delete(cacheKey);
                if (logPreload && LOGGING.ENABLED && LOGGING.DEBUG) {
                    console.warn(`[Image Cache] Failed to preload carousel image ${imageId}`);
                }
            });
        });

        // Wait for all images to preload (or fail)
        await Promise.all(preloadPromises);

        if (logPreload && LOGGING.ENABLED && LOGGING.DEBUG) {
            console.log(`[Image Cache] Preloaded ${preloadPromises.length} carousel images`);
        }
    } catch (error) {
        console.warn(`[Image Cache] Failed to preload carousel images:`, error);
    }
}

/**
 * Preloads all lightbox images for smooth navigation.
 * Preloads all images in the lightbox when it opens.
 * 
 * @param {Array} images - Array of image objects with `id` property
 * @param {boolean} [logPreload=false] - Whether to log preload actions (for debugging)
 * @returns {Promise<void>} - Resolves when all images are preloaded
 */
export async function preloadAllLightboxImages(images, logPreload = false) {
    if (!images || !Array.isArray(images) || images.length === 0) {
        return Promise.resolve();
    }

    try {
        // Dynamically import image utils to avoid circular dependencies
        const { getBlogImagePaths } = await import('../image-utils.js');

        const preloadPromises = images.map((imageData) => {
            if (!imageData || !imageData.id) {
                return Promise.resolve();
            }

            const imageId = imageData.id;
            const cacheKey = `lightbox_${imageId}`;

            // Skip if already preloaded or in-flight
            if (preloadedImageCache.has(cacheKey) || inFlightRequests.has(cacheKey)) {
                return Promise.resolve();
            }

            inFlightRequests.add(cacheKey);
            const imagePaths = getBlogImagePaths(imageId, 'webp');

            // Use fetch() for preloading instead of new Image()
            // This makes the initiator show as "image-cache.js" instead of "Other"
            // The images are cached, so subsequent DOM <img> requests will be cache hits
            return fetch(imagePaths.webp, {
                method: 'GET',
                cache: 'force-cache' // Force browser to use cache
            }).then(() => {
                preloadedImageCache.add(cacheKey);
                inFlightRequests.delete(cacheKey);
                if (logPreload && LOGGING.ENABLED && LOGGING.DEBUG) {
                    console.log(`[Image Cache] Preloaded lightbox image ${imageId}`);
                }
            }).catch(() => {
                // Don't mark as preloaded on error - let lightbox handle it
                inFlightRequests.delete(cacheKey);
                if (logPreload && LOGGING.ENABLED && LOGGING.DEBUG) {
                    console.warn(`[Image Cache] Failed to preload lightbox image ${imageId}`);
                }
            });
        });

        // Wait for all images to preload (or fail)
        await Promise.all(preloadPromises);

        if (logPreload && LOGGING.ENABLED && LOGGING.DEBUG) {
            console.log(`[Image Cache] Preloaded ${preloadPromises.length} lightbox images`);
        }
    } catch (error) {
        console.warn(`[Image Cache] Failed to preload lightbox images:`, error);
    }
}

/**
 * Preloads marker popup images for both carousel and lightbox.
 * This is the single source of truth for marker popup image preloading.
 * Automatically handles both carousel (smaller) and lightbox (bigger) versions.
 * Waits for images to actually load before resolving to ensure browser cache is ready.
 * 
 * @deprecated Use preloadAllCarouselImages() and preloadAllLightboxImages() instead for better performance
 * @param {Array} images - Array of image objects with `id` property (from marker popup)
 * @param {boolean} [logPreload=false] - Whether to log preload actions (for debugging)
 * @returns {Promise<void>} - Resolves when all images are preloaded
 */
export async function preloadMarkerPopupImages(images, logPreload = false) {
    if (!images || !Array.isArray(images) || images.length === 0) {
        return Promise.resolve();
    }

    // Dynamically import image utils to avoid circular dependencies
    const { getCardImagePaths, getBlogImagePaths } = await import('../image-utils.js');

    // Preload carousel images (smaller, for popup carousel) - wait for completion
    await preloadImages(images, getCardImagePaths, 'carousel', logPreload);

    // Preload lightbox images (bigger, for fullscreen lightbox) - wait for completion
    await preloadImages(images, getBlogImagePaths, 'lightbox', false);
}

/**
 * Clears the image preload cache (useful for testing or memory management)
 */
export function clearImagePreloadCache() {
    preloadedImageCache.clear();
    inFlightRequests.clear();
}

/**
 * Checks if an image has been preloaded
 * @param {number} imageId - The image ID
 * @param {string} cachePrefix - The cache prefix used
 * @returns {boolean} - True if image has been preloaded
 */
export function isImagePreloaded(imageId, cachePrefix = 'image') {
    return preloadedImageCache.has(`${cachePrefix}_${imageId}`);
}

/**
 * Preloads the favicon to prevent multiple reloads.
 * Should be called once at app startup.
 * Uses multiple strategies to ensure the favicon is cached and not re-requested.
 * 
 * @param {string} [faviconPath='/images/logo/logo_fav_icon.png'] - Path to favicon
 * @returns {Promise<void>} - Resolves when favicon is preloaded
 */
export function preloadFavicon(faviconPath = '/images/logo/logo_fav_icon.png') {
    const cacheKey = `favicon_${faviconPath}`;

    // Skip if already preloaded or in-flight
    if (preloadedImageCache.has(cacheKey) || inFlightRequests.has(cacheKey)) {
        return Promise.resolve();
    }

    inFlightRequests.add(cacheKey);

    // Strategy 1: Add <link rel="preload"> tag to document head
    // This tells the browser to preload and cache the favicon
    if (typeof document !== 'undefined' && document.head) {
        // Check if preload link already exists
        const existingPreload = document.querySelector(`link[rel="preload"][href="${faviconPath}"]`);
        if (!existingPreload) {
            const preloadLink = document.createElement('link');
            preloadLink.rel = 'preload';
            preloadLink.as = 'image';
            preloadLink.href = faviconPath;
            preloadLink.type = 'image/png';
            document.head.appendChild(preloadLink);
        }
    }

    // Strategy 2: Preload using fetch() to ensure it's in HTTP cache
    // This is more reliable than Image() for ensuring browser cache
    const fetchPromise = fetch(faviconPath, {
        method: 'GET',
        cache: 'force-cache' // Force browser to use cache
    }).then(() => {
        if (LOGGING.ENABLED && LOGGING.DEBUG) {
            console.log(`[Image Cache] Favicon fetched and cached: ${faviconPath}`);
        }
    }).catch(() => {
        // Silently fail - not critical
    });

    // Strategy 3: Also use Image() preload for additional caching
    const imagePromise = new Promise((resolve) => {
        const preloadImg = new Image();

        preloadImg.onload = () => {
            if (LOGGING.ENABLED && LOGGING.DEBUG) {
                console.log(`[Image Cache] Favicon image preloaded: ${faviconPath}`);
            }
            resolve();
        };

        preloadImg.onerror = () => {
            resolve(); // Resolve anyway to not block
        };

        preloadImg.src = faviconPath;
    });

    // Wait for both strategies to complete
    return Promise.all([fetchPromise, imagePromise]).then(() => {
        preloadedImageCache.add(cacheKey);
        inFlightRequests.delete(cacheKey);
        if (LOGGING.ENABLED && LOGGING.DEBUG) {
            console.log(`[Image Cache] Favicon preload complete: ${faviconPath}`);
        }
    });
}
